/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { StartRole } from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import { useEffect, useRef } from 'react'
import Text from 'src/components/ts-components/Text'
import DivisionCard from '../../../DivisionCard'

interface Props {
  divisions: Division[]
  selectedDivisionId?: string | null
  onSelect: (division: Division) => void
  currencyName: string
  /**
   * Set when the reader has just returned to this tab, so the current choice
   * can be scrolled back into view instead of leaving them at the top of a long
   * list wondering whether it was lost.
   */
  scrollToSelectionKey?: number
  /**
   * The side the reader is registering as. A player has no use for team fees,
   * team deposits or the "N teams registered" badge — and vice versa — so the
   * role, not the division's own flags, decides which half of the card shows.
   */
  role: StartRole | null
}

/**
 * The division grid.
 *
 * The list arriving here is already narrowed by role and by the age/gender/day
 * filters, so anything shown is something the reader can actually pick.
 */
const DivisionsTab = ({
  divisions,
  selectedDivisionId,
  onSelect,
  currencyName,
  scrollToSelectionKey,
  role,
}: Props) => {
  const selectedRef = useRef<HTMLDivElement | null>(null)

  // Two tabs are two views of one answer, so coming back to this one must show
  // the answer rather than the top of the list.
  useEffect(() => {
    if (!scrollToSelectionKey || !selectedRef.current) {
      return
    }
    selectedRef.current.scrollIntoView({ block: 'nearest' })
  }, [scrollToSelectionKey, selectedDivisionId])

  if (divisions.length === 0) {
    return (
      <div css={emptyStyle}>
        <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
          No Divisions Match Your Filters
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_40}
        >
          Try clearing a filter, or switch what you are registering.
        </Text>
      </div>
    )
  }

  return (
    <div css={gridStyle}>
      {divisions.map((division) => {
        const isSelected = selectedDivisionId === division.id
        return (
          <div
            key={division.id}
            ref={isSelected ? selectedRef : undefined}
            css={cellStyle}
          >
            <DivisionCard
              division={division}
              isSelected={isSelected}
              currencyName={currencyName}
              onSelect={onSelect}
              // Show only the half of the fee block that applies to the reader.
              // With no role chosen yet, fall back to what the division offers.
              isPlayerRegistrationEnabled={
                role
                  ? role === 'player'
                  : Boolean(division?.is_player_registration)
              }
              isTeamRegistrationEnabled={
                role ? role === 'team' : Boolean(division?.is_team_registration)
              }
              hasSelectedDivision={Boolean(selectedDivisionId)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default DivisionsTab

const gridStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  /* Every card in a row matches the tallest, so the grid reads as a grid
     rather than a ragged stack. */
  align-items: stretch;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Three across once there is room. The role filter removes half the fee
     block, which is what makes a narrower card work. */
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

/* The cell stretches with the row; this makes the card fill it rather than
   sitting at its own content height. */
const cellStyle = css`
  display: flex;

  > * {
    height: 100%;
  }
`

const emptyStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 48px 16px;
  border: 1px dashed ${colors.NEUTRAL_600};
  border-radius: 8px;
  text-align: center;
`
